import { CHART_RAMP } from '../styles/chartRamp';
import type { TimePeriod, UsageDay } from '../types/api';

// Plot geometry (#630 §5). The y-axis is fixed at 32 agent-hours and the ratio
// axis at 0–6.5×, both independent of the data so bars and line stay comparable
// across ranges.
const PLOT_HEIGHT = 170;
const Y_MAX_HOURS = 32;
const RATIO_MAX = 6.5;
const LEFT_GUTTER = 36;
const RIGHT_GUTTER = 96;

// The two agent lanes read off the shared chart ramp so the bars match the
// spend card's composition bars. main = orange-500 (ramp[0]); sub = the ramp's
// second-lightest orange (orange-300, ramp[1]).
const MAIN_COLOR = CHART_RAMP[0];
const SUB_COLOR = CHART_RAMP[1];

function agentHours(d: UsageDay): number {
  return (d.mainHours ?? 0) + (d.subHours ?? 0);
}

// Daily leverage = (main + sub) / driving, per day, from the payload (not
// smoothed). Undefined driving with agent time is infinite leverage, clamped to
// the axis ceiling; a fully idle day reads 0.
function dailyLeverage(d: UsageDay): number {
  const agent = agentHours(d);
  const drive = d.drivingHours ?? 0;
  if (drive <= 0) return agent > 0 ? RATIO_MAX : 0;
  return Math.min(agent / drive, RATIO_MAX);
}

// Ratio → SVG y in the 0..170 viewBox (0× at the baseline, 6.5× at the top).
function ratioY(r: number): number {
  return PLOT_HEIGHT - (Math.min(r, RATIO_MAX) / RATIO_MAX) * PLOT_HEIGHT;
}

// Main/sub bar segment heights, clamped so a heavy multi-agent day (>32h
// combined) never spills past the plot top and overlaps the eyebrow label
// (#715). The stack is capped at PLOT_HEIGHT while preserving the main/sub
// split, mirroring how the leverage line clamps to its ceiling.
function barHeights(d: UsageDay): { mainH: number; subH: number } {
  const mainH = (Math.max(d.mainHours ?? 0, 0) / Y_MAX_HOURS) * PLOT_HEIGHT;
  const subH = (Math.max(d.subHours ?? 0, 0) / Y_MAX_HOURS) * PLOT_HEIGHT;
  const total = mainH + subH;
  if (total <= PLOT_HEIGHT) return { mainH, subH };
  const scale = PLOT_HEIGHT / total;
  return { mainH: mainH * scale, subH: subH * scale };
}

// Tick cadence by range (§5): every day at 7d, every 4th at 30d, every 15th at
// all-time. Non-tick columns render a blank label so the flex cells stay aligned.
function tickStepFor(range: TimePeriod): number {
  switch (range) {
    case '7days':
      return 1;
    case 'all':
      return 15;
    default:
      return 4;
  }
}

// "YYYY-MM-DD" → "M/D" (no timezone math — the date is already a UTC day key).
function tickLabel(date?: string): string {
  if (!date) return '';
  const parts = date.split('-');
  if (parts.length !== 3) return '';
  return `${parseInt(parts[1], 10)}/${parseInt(parts[2], 10)}`;
}

/**
 * DailyLeverageChart (#630 §5) — the only hand-drawn element on the Usage
 * screen. Per-day stacked bars (main + subagent agent-hours) with a leverage
 * line overlaid in a non-uniformly scaled SVG, plus two dashed reference lines
 * (period average and the 1× break-even). Every stroke carries
 * `vector-effect="non-scaling-stroke"` so the line does not smear when the
 * viewBox scales to the container width.
 */
// The eyebrow header, shared by the populated chart and the empty state so an
// empty range still reads as "Daily leverage, nothing here" rather than a bare
// card.
function LeverageEyebrow() {
  return (
    <div className="mb-4 flex items-center gap-2">
      <span
        aria-hidden
        className="inline-block rounded-full"
        style={{ width: 6, height: 6, background: 'var(--color-orange-500)' }}
      />
      <span className="text-xs font-semibold uppercase tracking-wider text-orange-600">
        Daily leverage
      </span>
    </div>
  );
}

export function DailyLeverageChart({ days, range }: { days: UsageDay[]; range: TimePeriod }) {
  const n = days.length;

  // A range with no days is genuinely empty (a new account, or "today" before
  // any activity). Drawing the empty plot leaves the two dashed reference lines
  // floating over a blank grid AND prints "No day fell below 1×" — which reads
  // as "great, you never dipped" when in fact there is nothing to show. Render
  // an explicit empty state instead (rule 1).
  if (n === 0) {
    return (
      <div className="gp-card gp-card--hairline">
        <LeverageEyebrow />
        <div className="font-mono text-sm text-navy-400">No agent activity in this range.</div>
      </div>
    );
  }

  // ≤10 days would render as isolated slivers at the 20px cap, so widen the
  // bars to 52px; tighten the gap only when the series is dense.
  const barMaxWidth = n <= 10 ? 52 : 20;
  const gap = n > 45 ? 2 : 3;
  const tickStep = tickStepFor(range);

  // Period-average leverage (the totals ratio shown on the hero card), and the
  // per-day polyline points across the 0..1000 x-axis.
  const totalAgent = days.reduce((a, d) => a + agentHours(d), 0);
  const totalDrive = days.reduce((a, d) => a + (d.drivingHours ?? 0), 0);
  const avgLeverage = totalDrive > 0 ? Math.min(totalAgent / totalDrive, RATIO_MAX) : 0;

  const points = days
    .map((d, i) => `${((i + 0.5) / n) * 1000},${ratioY(dailyLeverage(d))}`)
    .join(' ');

  const belowOne = days.filter((d) => (d.drivingHours ?? 0) > 0 && dailyLeverage(d) < 1).length;
  const caption =
    belowOne === 0
      ? 'No day fell below 1×'
      : `${belowOne} day${belowOne === 1 ? '' : 's'} fell below 1× — you did more of the work than your agents`;

  const avgY = ratioY(avgLeverage);
  const breakEvenY = ratioY(1);

  return (
    <div className="gp-card gp-card--hairline">
      <LeverageEyebrow />

      {/* Plot row: 36px gutter · flexible plot · 96px gutter (for the ref labels). */}
      <div className="flex">
        <div style={{ width: LEFT_GUTTER }} aria-hidden />

        <div className="relative" style={{ flex: '1 1 0', minWidth: 0 }}>
          <div
            className="flex items-end"
            style={{ height: PLOT_HEIGHT, gap, borderBottom: '1px solid var(--gp-card-hairline)' }}
            role="img"
            aria-label="Daily agent-hours (main and subagent) with a leverage overlay"
          >
            {days.map((d, i) => {
              const { mainH, subH } = barHeights(d);
              const sub = (d.subHours ?? 0) > 0;
              return (
                <div
                  key={d.date ?? i}
                  className="flex flex-col items-center justify-end"
                  style={{ flex: '1 1 0', minWidth: 0, height: '100%' }}
                >
                  <div
                    data-testid="dlc-bar"
                    className="flex w-full flex-col justify-end"
                    style={{ maxWidth: barMaxWidth }}
                  >
                    <div
                      style={{
                        height: subH,
                        background: SUB_COLOR,
                        borderRadius: '2px 2px 0 0',
                      }}
                    />
                    <div
                      style={{
                        height: mainH,
                        background: MAIN_COLOR,
                        borderRadius: sub ? 0 : '2px 2px 0 0',
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Leverage overlay. viewBox scales non-uniformly, so every stroke is
              non-scaling to avoid smearing. */}
          <svg
            className="pointer-events-none absolute inset-0"
            width="100%"
            height={PLOT_HEIGHT}
            viewBox={`0 0 1000 ${PLOT_HEIGHT}`}
            preserveAspectRatio="none"
            aria-hidden
          >
            <line
              x1="0"
              x2="1000"
              y1={avgY}
              y2={avgY}
              stroke="var(--color-navy-100)"
              strokeWidth={1}
              strokeDasharray="4 4"
              vectorEffect="non-scaling-stroke"
            />
            <line
              x1="0"
              x2="1000"
              y1={breakEvenY}
              y2={breakEvenY}
              stroke="var(--color-rose-300)"
              strokeWidth={1}
              strokeDasharray="4 4"
              vectorEffect="non-scaling-stroke"
            />
            {n > 0 && (
              <polyline
                points={points}
                fill="none"
                stroke="var(--color-navy-900)"
                strokeWidth={1.75}
                strokeLinejoin="round"
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
              />
            )}
          </svg>
        </div>

        {/* Right gutter: reference-line labels at the matching y. */}
        <div className="relative" style={{ width: RIGHT_GUTTER, height: PLOT_HEIGHT }} aria-hidden>
          <span
            className="absolute font-mono text-[10px] tabular-nums text-navy-400"
            style={{ left: 8, top: avgY, transform: 'translateY(-50%)' }}
          >
            avg {avgLeverage.toFixed(1)}×
          </span>
          <span
            className="absolute font-mono text-[10px] tabular-nums text-navy-400"
            style={{ left: 8, top: breakEvenY, transform: 'translateY(-50%)' }}
          >
            1× break-even
          </span>
        </div>
      </div>

      {/* X-axis tick row, same three-column structure so ticks stay centered. */}
      <div className="mt-2 flex">
        <div style={{ width: LEFT_GUTTER }} aria-hidden />
        <div className="flex" style={{ flex: '1 1 0', minWidth: 0, gap }}>
          {days.map((d, i) => (
            <span
              key={d.date ?? i}
              data-testid="dlc-tick"
              className="overflow-hidden text-center font-mono text-[10px] tabular-nums text-navy-400"
              style={{ flex: '1 1 0', minWidth: 0 }}
            >
              {i % tickStep === 0 ? tickLabel(d.date) : ''}
            </span>
          ))}
        </div>
        <div style={{ width: RIGHT_GUTTER }} aria-hidden />
      </div>

      <p className="mt-3 text-sm text-navy-400">{caption}</p>
    </div>
  );
}
